import { useState, useEffect, useRef } from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router';
import { ArrowLeft, Trash2, UserPlus, Search, Loader2, Users, Activity, CheckCircle2, Flame } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { KpiCard } from '@/components/analytics/kpi-card';
import { TimeRangeTabs } from '@/components/analytics/time-range-tabs';
import { AssignmentsBarChart } from '@/components/analytics/assignments-bar-chart';
import { CohortCompletionChart } from '@/components/analytics/cohort-completion-chart';
import { StreakDistributionChart } from '@/components/analytics/streak-distribution-chart';
import { NeedsAttentionTable } from '@/components/analytics/needs-attention-table';
import { LeaderboardTable } from '@/components/analytics/leaderboard-table';
import { useTeacherDetail, useAssignStudent, useRemoveStudentFromTeacher, useSearchStudents } from '@/hooks/use-admin';
import { useTeacherAnalytics } from '@/hooks/use-analytics';
import { useAuthContext } from '@/contexts/auth-context';
import { formatDate, formatDateTime } from '@/lib/utils';
import { formatPercentRate } from '@/lib/analytics-format';
import { toast } from 'sonner';
import { isAxiosError } from 'axios';
import type { StudentSearchItem } from '@/types/admin';
import type { RangeParam, SeedAssignmentEntry } from '@/types/analytics';

function getInvitationStatus(inv: { used_by: string | null; expires_at: string }): {
  label: string;
  variant: 'default' | 'secondary' | 'destructive';
} {
  if (inv.used_by) return { label: 'Used', variant: 'default' };
  if (new Date(inv.expires_at) < new Date()) return { label: 'Expired', variant: 'destructive' };
  return { label: 'Active', variant: 'secondary' };
}

export default function TeacherDetailPage() {
  const { user } = useAuthContext();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const safeId = id ?? '';
  const { data, isLoading, error } = useTeacherDetail(safeId);
  const [range, setRange] = useState<RangeParam>('30d');
  const { data: analytics, isLoading: analyticsLoading } = useTeacherAnalytics(safeId, range);
  const assignMutation = useAssignStudent();
  const removeMutation = useRemoveStudentFromTeacher();
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedStudent, setSelectedStudent] = useState<StudentSearchItem | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { data: searchResults, isLoading: isSearching } = useSearchStudents(debouncedSearch);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  if (!id) {
    return <Navigate to="/teachers" replace />;
  }

  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  if (error) {
    const message =
      isAxiosError(error) && error.response?.data?.error?.message
        ? error.response.data.error.message
        : 'Teacher not found';
    return (
      <div className="space-y-4">
        <Button variant="ghost" render={<Link to="/teachers" />}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Teachers
        </Button>
        <p className="text-destructive">{message}</p>
      </div>
    );
  }

  if (!data) return null;

  const displayName = [data.first_name, data.last_name].filter(Boolean).join(' ') || data.email || 'Teacher';

  const handleAssign = () => {
    if (!selectedStudent) return;
    assignMutation.mutate(
      { teacherID: id, studentID: selectedStudent.id },
      {
        onSuccess: () => {
          toast.success('Student assigned');
          setAssignDialogOpen(false);
          setSelectedStudent(null);
          setSearchQuery('');
        },
        onError: (err) => {
          const msg =
            isAxiosError(err) && err.response?.data?.error?.message
              ? err.response.data.error.message
              : 'Failed to assign student';
          toast.error(msg);
        },
      },
    );
  };

  const formatStudentName = (s: StudentSearchItem) => {
    const name = [s.first_name, s.last_name].filter(Boolean).join(' ');
    return name || s.username || s.email || s.id;
  };

  const handleRemove = (studentID: string) => {
    removeMutation.mutate(
      { teacherID: id, studentID },
      {
        onSuccess: () => toast.success('Student removed'),
        onError: () => toast.error('Failed to remove student'),
      },
    );
  };

  return (
    <div className="space-y-6">
      <Button variant="ghost" render={<Link to="/teachers" />}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Teachers
      </Button>

      {/* Profile Card */}
      <Card>
        <CardHeader>
          <CardTitle>{displayName}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
            <div>
              <dt className="text-muted-foreground">Email</dt>
              <dd className="font-medium">{data.email ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Tier</dt>
              <dd><Badge variant="secondary">{data.tier}</Badge></dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Students</dt>
              <dd className="font-medium">{data.student_count}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Registered</dt>
              <dd className="font-medium">{formatDate(data.created_at)}</dd>
            </div>
            {data.bio && (
              <div className="col-span-2">
                <dt className="text-muted-foreground">Bio</dt>
                <dd>{data.bio}</dd>
              </div>
            )}
            {data.speciality && (
              <div className="col-span-2">
                <dt className="text-muted-foreground">Speciality</dt>
                <dd>{data.speciality}</dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {/* Tabs: Analytics | Students | Invitations */}
      <Tabs defaultValue="analytics">
        <TabsList>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="students">Students</TabsTrigger>
          <TabsTrigger value="invitations">Invitations</TabsTrigger>
        </TabsList>

        <TabsContent value="analytics" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <TimeRangeTabs value={range} onChange={setRange} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              title="Total students"
              value={analytics?.kpis.total_students ?? '—'}
              subtitle={`${analytics?.kpis.active_students_7d ?? 0} active (7d)`}
              icon={Users}
              isLoading={analyticsLoading}
            />
            <KpiCard
              title="Assignments"
              value={analytics?.kpis.assignments_created ?? '—'}
              subtitle={`${analytics?.kpis.assignments_done ?? 0} completed`}
              icon={Activity}
              isLoading={analyticsLoading}
            />
            <KpiCard
              title="Cohort completion"
              value={
                analytics ? formatPercentRate(analytics.kpis.cohort_completion_rate) : '—'
              }
              subtitle={`avg streak ${analytics?.kpis.avg_cohort_streak.toFixed(1) ?? '—'}d`}
              icon={CheckCircle2}
              isLoading={analyticsLoading}
            />
            <KpiCard
              title="Needs attention"
              value={analytics?.kpis.students_needing_attention ?? '—'}
              subtitle="no activity in 7+ days"
              icon={Flame}
              isLoading={analyticsLoading}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Assignments: created vs completed</CardTitle>
            </CardHeader>
            <CardContent>
              {analyticsLoading ? (
                <Skeleton className="h-[240px] w-full" />
              ) : (
                <AssignmentsBarChart data={analytics?.assignments_series ?? []} />
              )}
            </CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Cohort completion</CardTitle>
              </CardHeader>
              <CardContent>
                {analyticsLoading ? (
                  <Skeleton className="h-[260px] w-full" />
                ) : (
                  <CohortCompletionChart data={analytics?.cohort_completion ?? []} />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Streak distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {analyticsLoading ? (
                  <Skeleton className="h-[200px] w-full" />
                ) : (
                  <StreakDistributionChart data={analytics?.streak_distribution ?? []} />
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Most-assigned seeds</CardTitle>
            </CardHeader>
            <CardContent>
              {analyticsLoading ? (
                <Skeleton className="h-[200px] w-full" />
              ) : (
                <LeaderboardTable<SeedAssignmentEntry>
                  rows={analytics?.most_assigned_seeds ?? []}
                  getKey={(r) => r.seed_id}
                  getHref={(r) => `/seeds/${r.seed_id}`}
                  emptyLabel="No assignments in this range"
                  columns={[
                    {
                      header: 'Seed',
                      cell: (r) => <span className="font-medium">{r.title}</span>,
                    },
                    { header: 'Category', cell: (r) => r.category },
                    {
                      header: 'Assigned',
                      cell: (r) => r.assignments_count,
                      className: 'text-right tabular-nums',
                    },
                    {
                      header: 'Completion',
                      cell: (r) => formatPercentRate(r.completion_rate),
                      className: 'text-right tabular-nums',
                    },
                  ]}
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Needs attention</CardTitle>
            </CardHeader>
            <CardContent>
              {analyticsLoading ? (
                <Skeleton className="h-[160px] w-full" />
              ) : (
                <NeedsAttentionTable rows={analytics?.needs_attention ?? []} />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="students" className="mt-4">

      {/* Students */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Students ({data.students.length})</CardTitle>
          <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
            <DialogTrigger render={<Button size="sm" />}>
              <UserPlus className="mr-2 h-4 w-4" />
              Assign Student
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Assign Student</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="studentSearch">Search student</Label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      ref={searchInputRef}
                      id="studentSearch"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setSelectedStudent(null);
                      }}
                      placeholder="Search by name, email, or username..."
                      className="pl-9"
                    />
                    {isSearching && (
                      <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                  </div>
                  {debouncedSearch.length >= 2 && !selectedStudent && (
                    <div className="max-h-48 overflow-y-auto rounded-md border">
                      {searchResults && searchResults.length > 0 ? (
                        searchResults.map((s) => (
                          <button
                            key={s.id}
                            type="button"
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                            onClick={() => {
                              setSelectedStudent(s);
                              setSearchQuery(formatStudentName(s));
                            }}
                          >
                            <div className="flex-1">
                              <div className="font-medium">{formatStudentName(s)}</div>
                              {(s.username || s.telegram_id) && (
                                <div className="text-xs text-muted-foreground">
                                  {s.username ? `@${s.username}` : `ID: ${s.telegram_id}`}
                                </div>
                              )}
                            </div>
                          </button>
                        ))
                      ) : !isSearching ? (
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                          No students found
                        </div>
                      ) : null}
                    </div>
                  )}
                  {selectedStudent && (
                    <div className="flex items-center gap-2 rounded-md border bg-accent/50 px-3 py-2 text-sm">
                      <div className="flex-1">
                        <span className="font-medium">{formatStudentName(selectedStudent)}</span>
                        {(selectedStudent.username || selectedStudent.telegram_id) && (
                          <span className="ml-2 text-muted-foreground">
                            {selectedStudent.username ? `@${selectedStudent.username}` : `ID: ${selectedStudent.telegram_id}`}
                          </span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => {
                          setSelectedStudent(null);
                          setSearchQuery('');
                          searchInputRef.current?.focus();
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
                <Button
                  className="w-full"
                  disabled={!selectedStudent || assignMutation.isPending}
                  onClick={handleAssign}
                >
                  {assignMutation.isPending ? 'Assigning...' : 'Assign'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.students.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      No students linked.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.students.map((s) => (
                    <TableRow
                      key={s.id}
                      className="cursor-pointer hover:bg-accent"
                      onClick={() => navigate(`/students/${s.id}`)}
                    >
                      <TableCell className="font-medium">
                        {[s.first_name, s.last_name].filter(Boolean).join(' ') || s.username || '—'}
                      </TableCell>
                      <TableCell>{s.email ?? '—'}</TableCell>
                      <TableCell>{formatDate(s.joined_at)}</TableCell>
                      <TableCell>
                        <Badge variant={s.status === 'active' ? 'secondary' : 'destructive'}>
                          {s.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemove(s.id);
                          }}
                          disabled={removeMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="invitations" className="mt-4">
      {/* Invitations */}
      <Card>
        <CardHeader>
          <CardTitle>Invitations ({data.invitations.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invitation Link</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.invitations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground">
                      No invitation codes.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.invitations.map((inv) => {
                    const status = getInvitationStatus(inv);
                    return (
                      <TableRow key={inv.id}>
                        <TableCell>
                          {inv.link ? (
                            <a
                              href={inv.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-mono text-sm text-blue-600 hover:underline break-all"
                            >
                              {inv.link}
                            </a>
                          ) : (
                            <span className="font-mono text-sm">{inv.code}</span>
                          )}
                        </TableCell>
                        <TableCell>{formatDateTime(inv.created_at)}</TableCell>
                        <TableCell>{formatDateTime(inv.expires_at)}</TableCell>
                        <TableCell>
                          <Badge variant={status.variant}>{status.label}</Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
